/**
 * Oppsummering — årgangen sett på tvers av bookingene (Atle, 3. september).
 *
 * > «Der økonomi er i dag kaller vi siden heller oppsummering. Her har vi flere undersider som skal
 * > vise oppsummering av all logistikk+++: Logistikk, alt av langtransport og lokaltransport.
 * > Overnatting, alt av hoteller og overnattinger. Mat, alt av middager fra artister. Økonomi,
 * > likt som i dag.
 * > På alle disse sidene må informasjonen sammenstilles slik at man kan bruke den i praksis. F.eks
 * > matlistene må vise hvem og hvor mange som spiser hver dag etc.»
 *
 * ---------------------------------------------------------------------------------------------
 * «SAMMENSTILT SLIK AT MAN KAN BRUKE DEN I PRAKSIS» ER KRAVET, OG DET STYRER FORMEN.
 *
 * Programpostens egne faner svarer på «hva skjer med DENNE acten». Denne siden svarer på et annet
 * spørsmål, og det er ikke de samme radene sortert annerledes: det er «hvor mange spiser torsdag»,
 * «hvem skal hentes på Gardermoen før kl. 14», «hvor mange rom har vi på Thon natt til lørdag».
 *
 * Derfor er hver underside GRUPPERT PER DAG med en sum øverst i hver dag. En flat liste over
 * hundre reiser sortert på tid er den samme informasjonen og ubrukelig på et produksjonsmøte.
 *
 * ---------------------------------------------------------------------------------------------
 * DAGEN ER ÅRGANGENS EGEN, IKKE LESERENS.
 *
 * Grupperingen skjer i SQL, i `editions.timezone`, av samme grunn som timeplanen på programposten
 * gjør det: et fly som lander 00:30 lokalt er den 14. for den som møter det og den 13. i UTC, og
 * feil overskrift sender noen til flyplassen på feil dag.
 *
 * ---------------------------------------------------------------------------------------------
 * EN LINJE TRENGER IKKE EN ACT.
 *
 * «F.eks at vi kan sette opp en transport eller overnatting for noen som ikke er en act.» Migrering
 * 0060 gjorde `performance_id` valgfri og la `edition_id` på begge tabellene. `performanceTitle`
 * er derfor nullbar her, og en rad uten act er ikke en feil — den er en lydtekniker som skal hentes.
 */
import { z } from 'zod';


/** Which sub-page. The URL carries it, and the shell reads it. */
/*
 * KART SIST, OG DET ER EN AVGJØRELSE OM HVA SIDEN ÅPNER PÅ.
 *
 * Atle, 17. september: kartet er ikke en egen side lenger, det er en fane her «inntil videre».
 * `SUMMARY_TABS[0]` er fanen siden lander på for den som ikke ber om en annen, og et halvferdig
 * kart er det siste man vil møte når man åpner Oppsummering. Rekkefølgen er derfor rangeringen:
 * logistikk, hotell, mat, penger — og så stedet det foregår.
 */
export const SUMMARY_TABS = ['logistics', 'stay', 'food', 'economy', 'map'] as const;
export const summaryTabSchema = z.enum(SUMMARY_TABS);
export type SummaryTab = z.infer<typeof summaryTabSchema>;

/**
 * Én dag, med det som skjer den dagen og en sum for den.
 *
 * `day` er `YYYY-MM-DD` på årgangens klokke — se headeren. Aldri et instant: en overskrift er en
 * dato, og et instant gjennom en annen tidssone er en annen dato.
 */
const dayGroup = <T extends z.ZodTypeAny>(item: T, id: string) =>
  z
    .object({
      day: z.string(),
      items: z.array(item),
      /** Hva dagen summerer til — se hver undersides eget felt. */
      total: z.number().int().nonnegative(),
    })
    .meta({ id });

// ------------------------------------------------------------------------------------------- //
// Logistikk
// ------------------------------------------------------------------------------------------- //

export const summaryTravelSchema = z
  .object({
    id: z.uuid(),
    /** Null for en reise som ikke hører til en booking — se headeren. */
    performanceId: z.uuid().nullable(),
    performanceTitle: z.string().nullable(),
    /** «For hvem», når raden ikke har en act — sak 78.1. Overskriften den ellers ikke ville hatt. */
    forWhom: z.string().nullable(),
    mode: z.string(),
    kind: z.string(),
    fromPlace: z.string().nullable(),
    toPlace: z.string().nullable(),
    departsAt: z.iso.datetime().nullable(),
    arrivesAt: z.iso.datetime().nullable(),
    /** Dag uten klokke (215.15) — vis dagen, ikke 00:00. */
    departsDateOnly: z.boolean(),
    arrivesDateOnly: z.boolean(),
    reference: z.string().nullable(),
    /** Bestillingsreferansen (PNR) — egen kolonne siden QA 13, punkt 78. */
    bookingReference: z.string().nullable(),
    driverName: z.string().nullable(),
    /**
     * Hvor mange som reiser på denne — det tallet et produksjonsmøte faktisk trenger.
     *
     * Regnet på serveren framfor som lengden på en liste klienten ikke får: en side som viste
     * navnene på hver passasjer på hver reise ville vært tre skjermer, og spørsmålet er «passer de
     * i bilen».
     */
    passengerCount: z.number().int().nonnegative(),
    /** Navnene, kappet — nok til å kjenne igjen hvem det gjelder uten å liste tredve. */
    passengerNames: z.array(z.string()),
  })
  .meta({ id: 'SummaryTravel' });
export type SummaryTravel = z.infer<typeof summaryTravelSchema>;

export const summaryLogisticsResponseSchema = z
  .object({
    /** `total` per dag er antall personer som skal fraktes den dagen. */
    days: z.array(dayGroup(summaryTravelSchema, 'SummaryTravelDay')),
    /** Hele årgangen, så toppen av siden kan si det uten å summere sider. */
    journeyCount: z.number().int().nonnegative(),
    passengerCount: z.number().int().nonnegative(),
  })
  .meta({ id: 'SummaryLogisticsResponse' });
export type SummaryLogisticsResponse = z.infer<typeof summaryLogisticsResponseSchema>;

// ------------------------------------------------------------------------------------------- //
// Overnatting
// ------------------------------------------------------------------------------------------- //

export const summaryStaySchema = z
  .object({
    id: z.uuid(),
    performanceId: z.uuid().nullable(),
    performanceTitle: z.string().nullable(),
    /** «For hvem», når raden ikke har en act — sak 78.1. Overskriften den ellers ikke ville hatt. */
    forWhom: z.string().nullable(),
    name: z.string(),
    address: z.string().nullable(),
    bookingReference: z.string().nullable(),
    checkIn: z.string().nullable(),
    checkOut: z.string().nullable(),
    roomCount: z.number().int().nonnegative(),
    /** Hvor mange senger som faktisk er tildelt noen — «tre rom, fire personer». */
    occupantCount: z.number().int().nonnegative(),
    /** Rom uten noen i seg. En seng ingen sover i er penger, og et gap verdt å se. */
    emptyRoomCount: z.number().int().nonnegative(),
  })
  .meta({ id: 'SummaryStay' });
export type SummaryStay = z.infer<typeof summaryStaySchema>;

export const summaryStayResponseSchema = z
  .object({
    /** Gruppert på INNSJEKK, og `total` er antall rom som sjekkes inn den dagen. */
    days: z.array(dayGroup(summaryStaySchema, 'SummaryStayDay')),
    stayCount: z.number().int().nonnegative(),
    roomCount: z.number().int().nonnegative(),
  })
  .meta({ id: 'SummaryStayResponse' });
export type SummaryStayResponse = z.infer<typeof summaryStayResponseSchema>;

// ------------------------------------------------------------------------------------------- //
// Mat
// ------------------------------------------------------------------------------------------- //

export const summaryDinnerSchema = z
  .object({
    id: z.uuid(),
    performanceId: z.uuid().nullable(),
    performanceTitle: z.string().nullable(),
    /** «For hvem», når raden ikke har en act — sak 78.1. Overskriften den ellers ikke ville hatt. */
    forWhom: z.string().nullable(),
    startsAt: z.iso.datetime().nullable(),
    /** Dag uten klokke (215.15) — vis dagen, ikke 00:00. */
    startsDateOnly: z.boolean(),
    location: z.string().nullable(),
    /**
     * «Matlistene må vise hvem og hvor mange som spiser hver dag.»
     *
     * `pax` er det gamle «Antall». `partySize` er de som spiser pluss tillegget (253.39, 0929):
     * hele reisefølget eller lista på middagen. Det er det tallet som telles; `pax` erstatter aldri
     * lista, og vises ved siden av når de to er uenige.
     */
    pax: z.number().int().nullable(),
    partySize: z.number().int().nonnegative(),
    /** Hva som serveres, i den rekkefølgen den er ført opp. */
    dishes: z.array(z.string()),
    /**
     * Allergier og kosthold blant dem som skal spise — hentet fra reisefølget på acten.
     *
     * Ikke et tall: «to vegetarianere» er ubrukelig, «Kari: nøtter, også spor» er det kjøkkenet
     * trenger. Tomt når ingen har noe registrert, hvilket også er et svar.
     */
    dietaryNotes: z.array(z.string()),
    /**
     * DE SAMME, FRA PERSONEN (grep A1, 0915): «Allergier og kosthold» fra kortet, som tekst (A4,
     * 1026). For alle i huset (1077).
     */
    diners: z.array(
      z
        .object({
          personId: z.uuid(),
          displayName: z.string(),
          food: z.string().nullable(),
        })
        .meta({ id: 'SummaryDiner' }),
    ),
  })
  .meta({ id: 'SummaryDinner' });
export type SummaryDinner = z.infer<typeof summaryDinnerSchema>;

export const summaryFoodResponseSchema = z
  .object({
    /** `total` per dag er hvor mange som skal spise den dagen — summen kjøkkenet planlegger etter. */
    days: z.array(dayGroup(summaryDinnerSchema, 'SummaryDinnerDay')),
    dinnerCount: z.number().int().nonnegative(),
    guestCount: z.number().int().nonnegative(),
  })
  .meta({ id: 'SummaryFoodResponse' });
export type SummaryFoodResponse = z.infer<typeof summaryFoodResponseSchema>;

// ------------------------------------------------------------------------------------------- //
// Query
// ------------------------------------------------------------------------------------------- //

/**
 * Alle tre undersidene tar den samme spørringen: ett år.
 *
 * INGEN PAGINERING, og det er en beslutning framfor en forglemmelse. En årgang har titalls reiser
 * og en håndfull hoteller — dette er ikke en liste som vokser med organisasjonen, den vokser med
 * ett arrangement. En markørpaginering over en dag-gruppert visning ville dessuten kunne dele en
 * dag i to sider, hvilket er den ene tingen grupperingen finnes for å hindre.
 */
export const summaryQuerySchema = z.object({ editionId: z.uuid() }).meta({ id: 'SummaryQuery' });
export type SummaryQuery = z.infer<typeof summaryQuerySchema>;

// ------------------------------------------------------------------------------------------- //
// Måltider som ikke hører til en act (sak 80.2)
// ------------------------------------------------------------------------------------------- //

/**
 * «Også måltider som ikke hører til en act, lagt inn direkte på Oppsummering.»
 *
 * ---------------------------------------------------------------------------------------------
 * HVORFOR DETTE LIGGER HER OG IKKE I ADVANCE-KONTRAKTEN.
 *
 * En middag på en booking opprettes med `createAdvanceDinnerRequest`, som navngir en SEKSJON og
 * svarer med hele advance-dokumentet — fordi det er skjermen den skrives fra. Et crewmåltid har
 * ingen seksjon, ingen act og ingen advance å svare med. Å presse begge inn i ett skjema ville gitt
 * en forespørsel hvor halvparten av feltene er ulovlige avhengig av den andre halvparten.
 *
 * Reise og overnatting gikk motsatt vei — der ble det ETT skjema med `oneOwner` — og forskjellen er
 * ekte: de to skriver til den samme tabellen med de samme feltene og svarer med den samme raden.
 * Middagen svarer med to forskjellige ting.
 *
 * Rettene og allergiene er ikke med: en crewmiddag er et antall og et tidspunkt, og menyen føres på
 * kjøkkenet. Blir det bedt om, er det `advance_dinner_dishes` som allerede finnes.
 */
const mealFields = {
  startsAt: z.iso.datetime().nullable(),
  /** 215.15: bare dagen i `startsAt` gjelder. Usendt er «med klokke». */
  startsDateOnly: z.boolean().optional(),
  location: z.string().trim().min(1).max(200).nullable(),
  /** Hvor mange som skal spise. Null er «ikke avklart», som er et svar og ikke en null. */
  pax: z.number().int().min(0).max(5000).nullable(),
  /** «Crew, lørdag», «Frivillige, lunsj». Overskriften raden ellers ikke ville hatt. */
  forWhom: z.string().trim().min(1).max(160).nullable(),
  note: z.string().trim().min(1).max(2000).nullable(),
};

export const createSummaryMealRequestSchema = z
  .object({ editionId: z.uuid(), ...mealFields })
  .meta({ id: 'CreateSummaryMealRequest' });
export type CreateSummaryMealRequest = z.infer<typeof createSummaryMealRequestSchema>;

/**
 * DELVIS, SOM REISE OG OVERNATTING (bølge 2). Et felt som ikke er med, blir stående. Før var alle
 * feltene påkrevde og tjenesten skrev hele linja, og `note` — som lesningen ikke har — ble
 * blanket av hver lagring fra skjemaet og av hver Tab i hurtigredigeringen.
 * `startsDateOnly` gjelder bare sammen med `startsAt`.
 */
export const updateSummaryMealRequestSchema = z
  .object(mealFields)
  .partial()
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be present.',
  })
  .meta({ id: 'UpdateSummaryMealRequest' });
export type UpdateSummaryMealRequest = z.infer<typeof updateSummaryMealRequestSchema>;
